// src/private/src/pages/app/clients/ClientSchedulePage.jsx
//
// Client-facing meeting booking page. Tier-driven quota, soft AI-agent
// gate (chips deep-link to /agent-chat?prompt=...), calendar slot
// picker, and a request form for Starter / over-quota clients.
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VideoCallIcon from '@mui/icons-material/VideoCall';

import {
  cancelBooking,
  createBooking,
  createMeetingRequest,
  fetchAvailability,
  fetchBookings,
  fetchMeetingHistory,
  fetchMeetingPolicy,
} from '@src/api/clientSchedule';
import { dayjs } from '@src/corelib';
import { useMessage } from '@src/context/common';


// "Quick action" chips on the AI gate card. Each one deep-links into
// /agent-chat with a pre-filled prompt — these are intentionally the
// topics clients most often want a meeting for but that the agent can
// actually answer over chat. Tweak freely.
const AGENT_QUICK_ACTIONS = [
  {
    label: 'Update ZIP codes',
    prompt: 'I need to update my ZIP code coverage. Can you help me?',
  },
  {
    label: 'Update budget',
    prompt: 'I want to change my monthly budget. What are my options?',
  },
  {
    label: 'How are we pacing?',
    prompt: 'How are we pacing this month? Show me spend vs. plan.',
  },
  {
    label: 'Recent lead quality',
    prompt: 'Walk me through the quality of leads from the last 7 days.',
  },
];


function ClientSchedulePage() {
  const { clientSlug } = useParams();
  const message = useMessage();
  const theme = useTheme();

  // Data
  const [policy, setPolicy] = useState(null);
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [meetingHistory, setMeetingHistory] = useState([]);

  // Async state
  const [loading, setLoading] = useState({
    policy: true,
    slots: false,
    bookings: true,
    history: true,
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // UI state
  const [calendarRevealed, setCalendarRevealed] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null); // YYYY-MM-DD
  const [pendingSlot, setPendingSlot] = useState(null); // ISO string
  const [bookingNotes, setBookingNotes] = useState('');
  const [guestEmails, setGuestEmails] = useState(''); // comma-separated

  // Request-form state (Starter / over quota)
  const [requestTopic, setRequestTopic] = useState('');
  const [requestBody, setRequestBody] = useState('');
  const [requestWindow, setRequestWindow] = useState('');
  const [requestSubmitted, setRequestSubmitted] = useState(false);

  // ────────────────────────────────────────────────────────────────────────
  // Loaders
  // ────────────────────────────────────────────────────────────────────────

  const loadPolicy = useCallback(async () => {
    setLoading((s) => ({ ...s, policy: true }));
    try {
      const res = await fetchMeetingPolicy(clientSlug);
      setPolicy(res.data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load meeting policy.');
    } finally {
      setLoading((s) => ({ ...s, policy: false }));
    }
  }, [clientSlug]);

  const loadBookings = useCallback(async () => {
    setLoading((s) => ({ ...s, bookings: true }));
    try {
      const res = await fetchBookings(clientSlug);
      setBookings(res.data || []);
    } catch (err) {
      // Soft-fail — page is still useful without history.
    } finally {
      setLoading((s) => ({ ...s, bookings: false }));
    }
  }, [clientSlug]);

  const loadMeetingHistory = useCallback(async () => {
    setLoading((s) => ({ ...s, history: true }));
    try {
      const res = await fetchMeetingHistory(clientSlug, { limit: 10 });
      setMeetingHistory(res.data?.meetings || []);
    } catch (err) {
      // Soft-fail — page is useful even if Zoom webhook isn't wired yet.
    } finally {
      setLoading((s) => ({ ...s, history: false }));
    }
  }, [clientSlug]);

  const loadSlots = useCallback(async () => {
    setLoading((s) => ({ ...s, slots: true }));
    try {
      const res = await fetchAvailability(clientSlug);
      const list = (res.data || []).map((s) => s.starts_at);
      setSlots(list);
      if (list.length > 0 && !selectedDay) {
        setSelectedDay(dayjs(list[0]).format('YYYY-MM-DD'));
      }
    } catch (err) {
      message.error(
        err?.response?.data?.detail || 'Could not load availability.',
      );
    } finally {
      setLoading((s) => ({ ...s, slots: false }));
    }
  }, [clientSlug, selectedDay, message]);

  useEffect(() => {
    loadPolicy();
    loadBookings();
    loadMeetingHistory();
  }, [loadPolicy, loadBookings, loadMeetingHistory]);

  // Lazy-load availability: only when the client passes the AI gate, or
  // immediately if the calendar is the only thing on the page.
  useEffect(() => {
    if (calendarRevealed) loadSlots();
  }, [calendarRevealed, loadSlots]);

  // ────────────────────────────────────────────────────────────────────────
  // Derived state
  // ────────────────────────────────────────────────────────────────────────

  const canSelfServe =
    policy?.allow_self_serve_booking && policy?.quota_remaining > 0;
  const showRequestForm =
    policy && !canSelfServe && policy?.allow_request_form;

  // Group slots by local YYYY-MM-DD so the day selector and time selector
  // can render quickly.
  const slotsByDay = useMemo(() => {
    const map = {};
    for (const iso of slots) {
      const key = dayjs(iso).format('YYYY-MM-DD');
      (map[key] = map[key] || []).push(iso);
    }
    return map;
  }, [slots]);

  const availableDays = useMemo(() => Object.keys(slotsByDay), [slotsByDay]);

  // ────────────────────────────────────────────────────────────────────────
  // Actions
  // ────────────────────────────────────────────────────────────────────────

  const handleBookingConfirm = async () => {
    if (!pendingSlot) return;
    // Split the comma-separated guest field into a clean email list.
    // Filter to anything that looks roughly email-shaped — final
    // validation happens server-side via DRF's EmailField.
    const parsedGuests = guestEmails
      .split(/[,;\s]+/)
      .map((e) => e.trim())
      .filter((e) => e && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));

    setSubmitting(true);
    try {
      await createBooking(clientSlug, {
        starts_at: pendingSlot,
        client_notes: bookingNotes,
        attendee_emails: parsedGuests,
      });
      message.success('Meeting booked. Check your email for the invite.');
      setPendingSlot(null);
      setBookingNotes('');
      setGuestEmails('');
      // Refresh everything so quota + history + slot list all update.
      await Promise.all([loadPolicy(), loadBookings(), loadSlots()]);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || 'Could not book that slot.';
      message.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (booking) => {
    if (!window.confirm('Cancel this meeting?')) return;
    try {
      const res = await cancelBooking(booking.id, { reason: '' });
      message.success(
        res.data?.quota_was_restored
          ? 'Meeting cancelled — your quota was restored.'
          : 'Meeting cancelled.',
      );
      await Promise.all([loadPolicy(), loadBookings()]);
      if (calendarRevealed) loadSlots();
    } catch (err) {
      message.error(
        err?.response?.data?.detail || 'Could not cancel meeting.',
      );
    }
  };

  const handleRequestSubmit = async () => {
    if (!requestTopic.trim()) {
      message.error('Please enter a topic.');
      return;
    }
    setSubmitting(true);
    try {
      await createMeetingRequest(clientSlug, {
        topic: requestTopic.trim(),
        body: requestBody.trim(),
        requested_window: requestWindow.trim(),
      });
      setRequestSubmitted(true);
      setRequestTopic('');
      setRequestBody('');
      setRequestWindow('');
      message.success('Request submitted — we\'ll follow up by email.');
    } catch (err) {
      message.error(
        err?.response?.data?.detail || 'Could not submit request.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────────

  if (loading.policy) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!policy) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          No meeting policy is configured for this client's tier.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 980, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Schedule a Meeting
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Office hours: Tuesdays & Wednesdays, 1:00&nbsp;–&nbsp;4:00&nbsp;PM ET
      </Typography>

      <QuotaStrip policy={policy} />

      {canSelfServe && !calendarRevealed && (
        <AIGateCard
          onContinue={() => setCalendarRevealed(true)}
          duration={policy.duration_minutes}
        />
      )}

      {canSelfServe && calendarRevealed && (
        <BookingPicker
          loading={loading.slots}
          policy={policy}
          slotsByDay={slotsByDay}
          availableDays={availableDays}
          selectedDay={selectedDay}
          onSelectDay={setSelectedDay}
          onSelectSlot={(iso) => setPendingSlot(iso)}
        />
      )}

      {showRequestForm && (
        <RequestForm
          policy={policy}
          topic={requestTopic}
          body={requestBody}
          window={requestWindow}
          submitted={requestSubmitted}
          submitting={submitting}
          onTopicChange={setRequestTopic}
          onBodyChange={setRequestBody}
          onWindowChange={setRequestWindow}
          onSubmit={handleRequestSubmit}
          onReset={() => setRequestSubmitted(false)}
        />
      )}

      <BookingHistory
        bookings={bookings}
        loading={loading.bookings}
        onCancel={handleCancel}
      />

      <MeetingHistoryPanel
        meetings={meetingHistory}
        loading={loading.history}
      />

      <ConfirmDialog
        open={Boolean(pendingSlot)}
        slot={pendingSlot}
        policy={policy}
        notes={bookingNotes}
        guests={guestEmails}
        submitting={submitting}
        onNotesChange={setBookingNotes}
        onGuestsChange={setGuestEmails}
        onCancel={() => {
          setPendingSlot(null);
          setBookingNotes('');
          setGuestEmails('');
        }}
        onConfirm={handleBookingConfirm}
      />
    </Box>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────

function QuotaStrip({ policy }) {
  const pct = policy.meetings_per_month
    ? Math.min(100, (policy.quota_used / policy.meetings_per_month) * 100)
    : 0;
  const resets = dayjs(policy.quota_resets_at).format('MMMM D');
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              {policy.tier_name} tier
            </Typography>
            <Typography variant="h6">
              {policy.quota_used} of {policy.meetings_per_month} meetings used this month
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {policy.meetings_per_month > 0
                ? `${policy.quota_remaining} remaining · ${policy.duration_minutes} min each · resets ${resets}`
                : 'Your tier does not include scheduled meetings — submit a request below.'}
            </Typography>
          </Box>
          {policy.meetings_per_month > 0 && (
            <Box sx={{ width: { xs: '100%', sm: 240 } }}>
              <LinearProgress
                variant="determinate"
                value={pct}
                color={policy.quota_remaining > 0 ? 'primary' : 'warning'}
              />
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}


function AIGateCard({ onContinue, duration }) {
  const theme = useTheme();
  return (
    <Card
      sx={{
        mb: 3,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        border: 1,
        borderColor: alpha(theme.palette.primary.main, 0.2),
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <SmartToyIcon color="primary" />
          <Typography variant="h6">Have a quick question?</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Most common questions get a faster answer from our AI agent. Try one of
          these — and only book a {duration}-minute meeting if you still need one.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {AGENT_QUICK_ACTIONS.map((action) => (
            <Chip
              key={action.label}
              label={action.label}
              component={Link}
              to={`/agent-chat?prompt=${encodeURIComponent(action.prompt)}`}
              clickable
              icon={<ChatBubbleOutlineIcon />}
              variant="outlined"
              color="primary"
            />
          ))}
        </Stack>
        <Button
          variant="text"
          endIcon={<EventAvailableIcon />}
          onClick={onContinue}
        >
          Continue to book a meeting
        </Button>
      </CardContent>
    </Card>
  );
}


function BookingPicker({
  loading,
  policy,
  slotsByDay,
  availableDays,
  selectedDay,
  onSelectDay,
  onSelectSlot,
}) {
  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading available slots…
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (availableDays.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Alert severity="info">
            No open slots in the next {policy.max_horizon_days} days. Try again
            later, or submit a request below if you need a specific time.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const slotsForSelected = selectedDay ? slotsByDay[selectedDay] || [] : [];

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Pick a time
        </Typography>

        <Typography variant="overline" color="text.secondary">
          Day
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
          {availableDays.map((day) => {
            const d = dayjs(day);
            return (
              <Chip
                key={day}
                label={`${d.format('ddd')} · ${d.format('MMM D')}`}
                onClick={() => onSelectDay(day)}
                color={selectedDay === day ? 'primary' : 'default'}
                variant={selectedDay === day ? 'filled' : 'outlined'}
              />
            );
          })}
        </Stack>

        <Typography variant="overline" color="text.secondary">
          Time (your timezone)
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
          {slotsForSelected.map((iso) => (
            <Chip
              key={iso}
              label={dayjs(iso).format('h:mm A')}
              onClick={() => onSelectSlot(iso)}
              clickable
              color="primary"
              variant="outlined"
            />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}


function ConfirmDialog({
  open,
  slot,
  policy,
  notes,
  guests,
  submitting,
  onNotesChange,
  onGuestsChange,
  onCancel,
  onConfirm,
}) {
  if (!slot) return null;
  const start = dayjs(slot);
  const end = start.add(policy.duration_minutes, 'minute');

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Confirm meeting</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              When
            </Typography>
            <Typography variant="body1">
              {start.format('dddd, MMMM D')}
            </Typography>
            <Typography variant="body1">
              {start.format('h:mm A')} – {end.format('h:mm A')} ({policy.duration_minutes} min)
            </Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">
              What you can expect
            </Typography>
            <Typography variant="body2">
              You'll get an email invite with the Zoom join link. Add anyone
              else who should attend below — they'll be invited too.
            </Typography>
          </Box>
          <TextField
            label="Add guests (optional)"
            value={guests}
            onChange={(e) => onGuestsChange(e.target.value)}
            fullWidth
            placeholder="email1@company.com, email2@company.com"
            helperText="Comma-separated email addresses. They'll get the same invite."
          />
          <TextField
            label="What would you like to discuss? (optional)"
            multiline
            minRows={3}
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            fullWidth
            placeholder="A short note helps us prep — e.g. 'Wondering about creative refresh strategy.'"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <EventAvailableIcon />}
        >
          Book meeting
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function RequestForm({
  policy,
  topic,
  body,
  window: windowText,
  submitted,
  submitting,
  onTopicChange,
  onBodyChange,
  onWindowChange,
  onSubmit,
  onReset,
}) {
  const isOverQuota =
    policy.allow_self_serve_booking && policy.quota_remaining === 0;
  const resets = dayjs(policy.quota_resets_at).format('MMMM D');

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Request a meeting
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isOverQuota
            ? `You've used your quota for this month — it resets on ${resets}. In the meantime, tell us what you need and we'll follow up by email.`
            : `Your tier doesn't include scheduled office-hours meetings. Submit a request and we'll follow up by email to find a time that works.`}
        </Typography>

        {submitted ? (
          <Alert severity="success" action={
            <Button color="inherit" size="small" onClick={onReset}>
              Submit another
            </Button>
          }>
            Request submitted. We'll follow up by email shortly.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <TextField
              label="What do you want to discuss?"
              value={topic}
              onChange={(e) => onTopicChange(e.target.value)}
              required
              fullWidth
              placeholder="e.g. Creative refresh strategy for Q3"
            />
            <TextField
              label="Details (optional)"
              value={body}
              onChange={(e) => onBodyChange(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              placeholder="Context that helps us prep ahead of time."
            />
            <TextField
              label="When would work for you? (optional)"
              value={windowText}
              onChange={(e) => onWindowChange(e.target.value)}
              fullWidth
              placeholder="e.g. Thursday afternoon, next week"
            />
            <Box>
              <Button
                variant="contained"
                onClick={onSubmit}
                disabled={submitting || !topic.trim()}
                startIcon={submitting ? <CircularProgress size={16} /> : null}
              >
                Submit request
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}


function BookingHistory({ bookings, loading, onCancel }) {
  if (loading) return null;
  if (!bookings || bookings.length === 0) return null;

  const now = dayjs();
  const upcoming = bookings.filter(
    (b) => b.status === 'active' && dayjs(b.starts_at).isAfter(now),
  );
  const past = bookings.filter((b) => !upcoming.includes(b));

  return (
    <Fragment>
      {upcoming.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Upcoming meetings
            </Typography>
            <Stack divider={<Divider flexItem />} spacing={2}>
              {upcoming.map((b) => (
                <BookingRow key={b.id} booking={b} onCancel={onCancel} />
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {past.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Past meetings
            </Typography>
            <Stack divider={<Divider flexItem />} spacing={2}>
              {past.slice(0, 5).map((b) => (
                <BookingRow key={b.id} booking={b} />
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Fragment>
  );
}


function MeetingHistoryPanel({ meetings, loading }) {
  // Hide entirely until we have data — keeps the page clean for new
  // clients before any Zoom summaries have rolled in.
  if (loading) return null;
  if (!meetings || meetings.length === 0) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Past meeting summaries
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Auto-generated by Zoom AI Companion. The AI agent uses these as context when answering your questions.
        </Typography>
        <Stack divider={<Divider flexItem />} spacing={2}>
          {meetings.map((m) => (
            <MeetingSummaryRow key={m.id} meeting={m} />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}


function MeetingSummaryRow({ meeting }) {
  const when = meeting.started_at ? dayjs(meeting.started_at) : null;
  const actionItems = Array.isArray(meeting.action_items) ? meeting.action_items : [];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="subtitle2">{meeting.title || 'Meeting'}</Typography>
        <Typography variant="caption" color="text.secondary">
          {when ? when.format('MMM D, h:mm A') : '—'}
          {meeting.duration_minutes ? ` · ${meeting.duration_minutes} min` : ''}
        </Typography>
      </Stack>
      {meeting.summary && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
          {meeting.summary}
        </Typography>
      )}
      {actionItems.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Action items:
          </Typography>
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
            {actionItems.slice(0, 5).map((item, idx) => {
              const text = typeof item === 'string' ? item : item?.text || JSON.stringify(item);
              return (
                <li key={idx}>
                  <Typography variant="body2" component="span">
                    {text}
                  </Typography>
                </li>
              );
            })}
          </ul>
        </Box>
      )}
      {meeting.transcript_url && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            href={meeting.transcript_url}
            target="_blank"
            rel="noreferrer"
          >
            View transcript
          </Button>
        </Box>
      )}
    </Box>
  );
}


function BookingRow({ booking, onCancel }) {
  const start = dayjs(booking.starts_at);
  const isPast = !onCancel;
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ sm: 'center' }}
      justifyContent="space-between"
    >
      <Box>
        <Typography variant="body1">
          {start.format('dddd, MMM D · h:mm A')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {booking.duration_minutes} min · {booking.status_display}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1}>
        {(booking.zoom_join_url || booking.gcal_meet_link) && (
          <Button
            size="small"
            startIcon={<VideoCallIcon />}
            href={booking.zoom_join_url || booking.gcal_meet_link}
            target="_blank"
            rel="noreferrer"
            disabled={isPast}
          >
            Join
          </Button>
        )}
        {onCancel && (
          <Button
            size="small"
            color="warning"
            onClick={() => onCancel(booking)}
          >
            Cancel
          </Button>
        )}
      </Stack>
    </Stack>
  );
}


export default ClientSchedulePage;
